import type React from "react";
import { useState, useEffect } from "react";
import "./feedback-widget.css";
import { FeedbackProps, ICON_SIZE_MAP } from "../../utils/types";
import { collectUserMetadata } from "../../utils/collect-metadata";
import { useLocalStorage } from "../../hooks/useLocalStorage";
import { useModal } from "../../hooks/useModel";
import Modal from "./components/Model";
import FeedbackForm from "./components/FeedbackForm";
import { FeedbackIcon } from "./components/icons/FeedbackIcon";
import { MessageIcon } from "./components/icons/MessageIcon";
import { ChatIcon } from "./components/icons/ChatIcon";
import { FeedauraIcon } from "./components/icons/FeedauraIcon";

const DEFAULT_API_ENDPOINT =
  process.env.NODE_ENV === "production"
    ? "https://feedaura.feedaura-ai.workers.dev/feedbacks"
    : "http://localhost:8787/feedbacks";

const LOCAL_STORAGE_KEY = "feedback-widget-text";

const Feedback: React.FC<FeedbackProps> = ({
  projectSecret,
  position = 'bottom-right',
  primaryColor = '#0F8CFF',
  secondaryColor = '#0070E0',
  textColor = '#333333',
  backgroundColor = '#FFFFFF',
  theme = 'light',
  buttonText = '',
  buttonIcon = 'feedaura',
  iconColor = '#FFFFFF',
  buttonSize = 'medium',
  buttonRadius = '50%',
  widgetStyle = 'floating',
  zIndex = 9999,
  autoCloseAfterSubmit = true,
  apiEndpoint = DEFAULT_API_ENDPOINT,
  animated = true,
  productName = 'product',
  placeholder = 'Please write here',
  submitText = 'Submit',
  headerText = 'Give us your feedback',
  questionText = `What was your experience while using ${productName}?`,
  feedbackLabelText = 'Write your feedback',
}) => {
  const [feedback, setFeedback] = useState({ text: "", emoji: "" });
  const [errorMsg, setErrorMsg] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [storedText, setStoredText] = useLocalStorage(LOCAL_STORAGE_KEY, "");
  const { isOpen, modalAnimation, openModal, closeModal, modalRef, triggerButtonRef } = useModal(() => setIsSubmitted(false));

  // Load saved feedback text on mount.
  useEffect(() => {
    if (storedText && feedback.text === "") {
      setFeedback((prev) => ({ ...prev, text: storedText }))
    }
  }, [])

  // Update local storage when feedback text changes.
  useEffect(() => {
    // Only update localStorage if the text has actually changed from what's stored
    if (feedback.text !== storedText) {
      setStoredText(feedback.text)
    }
  }, [feedback.text, setStoredText, storedText])

  // Auto-close modal after submission if enabled.
  useEffect(() => {
    if (isSubmitted && autoCloseAfterSubmit) {
      const timer = setTimeout(() => {
        closeModal();
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [isSubmitted, autoCloseAfterSubmit, closeModal]);

  const handleSubmit = async () => {
    if (!projectSecret) {
      setErrorMsg("Project secret is missing.");
      return;
    }
    if (!apiEndpoint) {
      setErrorMsg("API endpoint is missing.");
      return;
    }
    if (feedback.text.trim() === "") {
      setErrorMsg("Feedback text is required.");
      return;
    }

    setErrorMsg("");
    setIsSubmitting(true);
    try {
      const metadata = await collectUserMetadata();
      const feedbackData = {
        text: feedback.text,
        ...(feedback.emoji ? { emoji: feedback.emoji } : {}),
      };
      const response = await fetch(apiEndpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          projectSecret,
          feedback: feedbackData,
          metadata,
        }),
      });
      if (response.ok) {
        setIsSubmitted(true);
        setStoredText("");
        setFeedback({ text: "", emoji: "" });
      } else {
        const errorData = await response.json();
        if (errorData?.code === "LIMIT_REACHED") {
          setErrorMsg("The author has run out of free credits and can't accept more feedback at this time.");
        } else {
          setErrorMsg(errorData?.message || "Failed to submit feedback. Please try again later.");
        }
      }
    } catch (error) {
      console.error("Error submitting feedback:", error);
      setErrorMsg("An error occurred while submitting feedback.");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Dynamic styles.
  const dynamicStyles = {
    "--primary-color": primaryColor,
    "--secondary-color": secondaryColor,
    "--text-color": textColor,
    "--background-color": backgroundColor,
    "--button-radius": buttonRadius,
    "--z-index": zIndex,
    "--icon-color": iconColor,
  } as React.CSSProperties;

  // Compute CSS classes.
  const buttonSizeClass = `button-${buttonSize}`;
  const positionClass = `position-${position}`;
  const themeClass = `theme-${theme}`;
  const styleClass = `style-${widgetStyle}`;

  const iconSize = ICON_SIZE_MAP[buttonSize];

  const renderButtonIcon = () => {
    const props = { size: iconSize, color: iconColor, animated };
    switch (buttonIcon) {
      case 'feedback':
        return <FeedbackIcon {...props} />;
      case 'message':
        return <MessageIcon {...props} />;
      case 'chat':
        return <ChatIcon {...props} />;
      case 'feedaura':
        return <FeedauraIcon {...props} />;
      case 'none':
      default:
        return null;
    }
  };

  return (
    <div className={`feedback-widget ${positionClass} ${themeClass} ${styleClass}`} style={dynamicStyles}>
      <button
        ref={triggerButtonRef}
        className={`feedback-button ${buttonSizeClass}`}
        onClick={openModal}
        style={{ backgroundColor: primaryColor, borderRadius: buttonRadius }}
        aria-label="Open feedback form"
      >
        {buttonIcon !== "none" && <span className="button-icon">
          {renderButtonIcon()}
        </span>}
        {buttonText && <span className="button-text">
          {buttonText}
        </span>}
      </button>
      {isOpen && (
        <Modal isOpen={isOpen} modalAnimation={modalAnimation} onClose={closeModal} modalRef={modalRef} headerText={headerText}>
          {isSubmitted ? (
            <div className="feedback-success" aria-live="polite">
              <div className="success-icon">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path
                    d="M22 11.08V12C21.9988 14.1564 21.3005 16.2547 20.0093 17.9818C18.7182 19.709 16.9033 20.9725 14.8354 21.5839C12.7674 22.1953 10.5573 22.1219 8.53447 21.3746C6.51168 20.6273 4.78465 19.2461 3.61096 17.4371C2.43727 15.628 1.87979 13.4881 2.02168 11.3363C2.16356 9.18455 2.99721 7.13631 4.39828 5.49706C5.79935 3.85781 7.69279 2.71537 9.79619 2.24013C11.8996 1.7649 14.1003 1.98232 16.07 2.85999"
                    stroke={primaryColor}
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M22 4L12 14.01L9 11.01"
                    stroke={primaryColor}
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
              <h4>Thank you for your feedback!</h4>
              <p>We appreciate your input and will use it to improve our service.</p>
            </div>
          ) : (
            <>
              <FeedbackForm
                feedback={feedback}
                setFeedback={setFeedback}
                errorMsg={errorMsg}
                setErrorMsg={setErrorMsg}
                primaryColor={primaryColor}
                placeholder={placeholder}
                feedbackLabel={feedbackLabelText}
                submitText={submitText}
                isSubmitting={isSubmitting}
                onSubmit={handleSubmit}
                questionText={questionText}
              />
            </>
          )}
        </Modal>
      )}
    </div>
  );
};

export default Feedback;
